import "filepond/dist/filepond.min.css";
import "./UIFilePond.scss";

import { FilePond, FilePondProps } from "react-filepond";
import { FilePondErrorDescription, FilePondFile, FilePondInitialFile, FilePondServerConfigProps } from "filepond";

import CommonUtil from "utils/CommonUtil";
import { DataJobTypes } from "domain/BaseEntity";
import ObjectUtil from "utils/ObjectUtil";
import React from "react";
import StringUtil from "utils/StringUtil";
import TbComFile from "domain/main/com/file/TbComFile";
import TbComFileDetail from "domain/main/com/file/detail/TbComFileDetail";
import TbComFileMapngDetail from "domain/main/com/file/mapng/TbComFileMapngDetail";
import { toast } from "react-toastify";
import userStore from "common/user/store/UserStore";

export interface UIFileUploadProps {
  value?: TbComFile;
  valueChanged?: (value: TbComFile) => void;
  policy?: string;
  attrs?: FilePondProps;
}

UIFileUpload.defaultProps = {
  policy: "default",
} as UIFileUploadProps;

export default UIFileUpload;

function UIFileUpload(props: UIFileUploadProps) {
  const tbComFileMapngDetailList: TbComFileMapngDetail[] = props.value?.tbComFileMapngDetailList || [];
  const [files, setFiles] = React.useState<Array<FilePondInitialFile | File | Blob | string>>([]);

  let filePondRef = React.useRef<FilePond>(null);

  React.useEffect(() => {
    if (props.value != null) {
      props.value["__upload_files__"] = async () => {
        // 업로드 도중에 Re-rendering 발생시 filePondRef 값 유지
        const _filePondRef = filePondRef;

        await filePondRef.current?.processFiles().then(() => {
          resetFileList(_filePondRef);
        });
      };
    }
  });

  React.useEffect(
    () => {
      setFiles(
        tbComFileMapngDetailList.map((tbComFileMapngDetail) => {
          tbComFileMapngDetail.JOB_TYPES = DataJobTypes.UPDATE;

          return {
            source: JSON.stringify(tbComFileMapngDetail),
            options: {
              type: "local",
              file: {
                name: tbComFileMapngDetail.getOrginlFileNm(),
                size: tbComFileMapngDetail.getFileSize(),
              },
            },
          } as FilePondInitialFile;
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.value]
  );

  const attrs: FilePondProps = { ...props.attrs };

  attrs.name = ObjectUtil.defaultIfNull(attrs.name, "files");
  attrs.allowDrop = ObjectUtil.defaultIfNull(attrs.allowDrop, true);
  attrs.allowReplace = ObjectUtil.defaultIfNull(attrs.allowReplace, false);
  attrs.instantUpload = ObjectUtil.defaultIfNull(attrs.instantUpload, false);
  attrs.allowReorder = ObjectUtil.defaultIfNull(attrs.allowReorder, true);
  attrs.allowMultiple = ObjectUtil.defaultIfNull(attrs.allowMultiple, true);
  attrs.labelIdle = ObjectUtil.defaultIfNull(attrs.labelIdle, "이곳에 파일을 드래그하거나 클릭하세요.");

  attrs["credits"] = false;

  const csrfHeaderName = userStore.csrfToken?.getHeaderName()||'';
  const csrfToken = userStore.csrfToken?.getToken()||'';

  const serverConfig: FilePondServerConfigProps = {
    server: {
      url: CommonUtil.contextPath + "/common/file/",
      process: {
        headers: {
          Accept: "application/json; charset=utf-8",
          [csrfHeaderName]: csrfToken,
        },
        url: "upload" + (props.policy == null ? "" : "?policy=" + props.policy),
        onerror: (responseBody: any): any => {
          if (typeof responseBody === "string") {
            try {
              return JSON.parse(responseBody);
            } catch (e) {
              return responseBody;
            }
          }

          return responseBody;
        },
      },
      revert: {
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "X-HTTP-Method-Override": "DELETE",
          [csrfHeaderName]: csrfToken,
        },
        url: "revert",
      },
    },
  };

  const resetFileList = (filePondRef) => {
    if (filePondRef == null) {
      return;
    }

    for (let i = tbComFileMapngDetailList.length - 1; i >= 0; i--) {
      if (tbComFileMapngDetailList[i].JOB_TYPES === DataJobTypes.CREATE) {
        tbComFileMapngDetailList.splice(i, 1);
      }
    }

    let sortOrdr = 0;

    filePondRef.getFiles().forEach((filePondFile: FilePondFile) => {
      if (filePondFile.serverId == null) {
        return;
      }

      const tbComFileDetailFromFile = new TbComFileMapngDetail(JSON.parse(filePondFile.serverId));

      let tbComFileMapngDetail: TbComFileMapngDetail | undefined;

      if (StringUtil.isNotEmpty(tbComFileDetailFromFile.getFileDetailId())) {
        tbComFileMapngDetail = tbComFileMapngDetailList.find(
          (tbComFileDetail) => tbComFileDetail.getFileDetailId() === tbComFileDetailFromFile.getFileDetailId()
        );
      }

      if (tbComFileMapngDetail == null) {
        tbComFileMapngDetailList.push((tbComFileMapngDetail = tbComFileDetailFromFile));
      }

      tbComFileMapngDetail.setSortOrdr(++sortOrdr);
    });
  };

  return (
    <FilePond
      ref={filePondRef}
      name="files"
      files={files}
      itemInsertLocation="after"
      {...attrs}
      server={serverConfig.server}
      labelFileProcessingError={(error: any): string => {
        if (error.body != null && error.body.message != null) {
          return error.body.message;
        }

        return "파일 업로드 에러가 발생했습니다.";
      }}
      onerror={(error: FilePondErrorDescription, filePondFile?: FilePondFile, status?: any): void => {
        if (error.body != null && error.body["message"] != null) {
          toast.error(error.body["message"]);
        } else {
          toast.error(status.main);
        }
      }}
      onupdatefiles={(filePondFiles: FilePondFile[]) => {
        const actualFileObjects = filePondFiles.map((filePondFile: FilePondFile) => filePondFile.file);

        //@ts-ignore
        props.value?.setFiles(actualFileObjects);

        setFiles(actualFileObjects);
      }}
      onreorderfiles={(filePondFiles: FilePondFile[]) => {
        resetFileList(filePondRef);
      }}
      onremovefile={(error: FilePondErrorDescription | null, filePondFile: FilePondFile) => {
        if (filePondFile.serverId == null) {
          return;
        }

        const tbComFileDetailRemoved = new TbComFileDetail(JSON.parse(filePondFile.serverId));
        const tbComFileDetail = tbComFileMapngDetailList.find(
          (tbComFileDetail) => tbComFileDetail.getFileDetailId() === tbComFileDetailRemoved.getFileDetailId()
        );

        if (tbComFileDetail != null) {
          tbComFileDetail.JOB_TYPES = DataJobTypes.DELETE;
        }
      }}
    />
  );
}
